/* global AbortSignal */
/** Клиент справочного блока «Склад» (backend `wh_ops/`, витрина dwh `mart.wh_ops_*`): «Аналитика МП» —
 *  `GET /mp-analytics/warehouse-ops` (право `ozon_analytics`, направления «МП» + «ФБС Москва», одни
 *  для Ozon и WB), «Аналитика ОПТ» — `GET /wholesale/warehouse-ops` (право `wholesale_analytics`,
 *  направление «Опт»). Не P&L и не маржа.
 *
 *  null ≠ 0: `amount` null — ни у одной строки нет суммы (тарифа нет, «без суммы»); объёмы разных
 *  операций не складываются; суммы — с НДС (решение владельца 23.10.2026, предварительно). 503 — витрины нет / сборка частичная: блок показывает текст у себя. */
import { api } from "./client";

export type WhDirection = "mp" | "opt" | "fbs" | "undefined";

/** Операция за период (Σ по дням витрины). */
export interface WhOperation {
  operation: string;
  label: string;
  unit: string;
  rows: number;
  days: number;
  /** Σ объёма по дням, где он есть; null — нет ни у одной строки */
  volume: number | null;
  volume_unknown_rows: number;
  /** Σ суммы по дням, где она есть; null — «без суммы» (не 0) */
  amount: number | null;
  unpriced_rows: number;
  unpriced_days: number;
  /** тариф за период один; null — нет тарифа или менялся (`tariff_varies`) */
  tariff: number | null;
  tariff_varies: boolean;
}

export interface WhTotal {
  /** Σ известных сумм несправочных строк; null — ни одной суммы */
  amount: number | null;
  rows: number;
  unpriced_rows: number;
  priced_complete: boolean;
}

export interface WhDirectionBlock {
  direction: WhDirection;
  title: string;
  operations: WhOperation[];
  /** «не считается» — объём справочно, в итог не входят */
  reference: WhOperation[];
  total: WhTotal;
}

export interface WhReason {
  code: string | null;
  /** причина есть в контракте; false — новая, показываем текст как есть */
  known: boolean;
  text: string;
  /** дни причины внутри периода (у причин блока — всегда, из `mart.wh_ops_days`); null — только в
   *  `state.incomplete_reasons`: причина на всё окно (`days: []` / нет ключа у портала склада) */
  days: string[] | null;
}

export interface WhOpsState {
  built_at: string;
  build_id: string;
  /** окно последней выгрузки склада */
  date_from: string;
  date_to: string;
  /** когда портал склада посчитал выгрузку */
  computed_at: string;
  complete: boolean;
  incomplete_reasons: WhReason[];
  days_loaded: number;
}

export interface WhOps {
  section: "mp" | "opt";
  date_from: string;
  date_to: string;
  directions: WhDirectionBlock[];
  total: WhTotal;
  /** направление «Не определено» за период — не в разделе, но видно */
  undirected: WhTotal & { reference_rows: number };
  /** полнота по дням периода (`mart.wh_ops_days`): нет `missing_days` и нет `reasons` */
  complete: boolean;
  missing_days: string[];
  reasons: WhReason[];
  state: WhOpsState;
  from_cache: boolean;
}

export async function getMpWhOps(dateFrom: string, dateTo: string, signal?: AbortSignal): Promise<WhOps> {
  const { data } = await api.get<WhOps>("/mp-analytics/warehouse-ops", {
    params: { date_from: dateFrom, date_to: dateTo },
    signal,
  });
  return data;
}

export async function getOptWhOps(monthFrom: string, monthTo: string, signal?: AbortSignal): Promise<WhOps> {
  const { data } = await api.get<WhOps>("/wholesale/warehouse-ops", {
    params: { month_from: monthFrom, month_to: monthTo },
    signal,
  });
  return data;
}
